import { Component } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Boundary LOCAL para chunks lazy. Sin esto, un fallo de red al descargar el
 * chunk (o un deploy que cambió los hashes de los bundles) escala al
 * ErrorBoundary global y reemplaza la app entera — perdiendo la vista con los
 * datos que ya tenía en pantalla, justo en el escenario degradado (red mala)
 * que más importa. Contiene el error al bloque que intentaba montarse y ofrece
 * recargar la página, que es además el remedio correcto post-deploy.
 *
 * Uso: <ChunkBoundary label={t('el detalle de la ruta')}><Suspense …>…
 */
class Boundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    // eslint-disable-next-line no-console
    console.error('ChunkBoundary: fallo cargando un chunk lazy:', error);
  }

  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

// La clase de arriba no puede usar hooks; el wrapper funcional resuelve t() y
// le pasa el fallback ya armado (además así los tests mockean useTranslation
// como en el resto del codebase, sin necesitar withTranslation).
export function ChunkBoundary({ label, children }) {
  const { t } = useTranslation();
  const fallback = (
    <div className="m-4 p-4 rounded-lg border border-border bg-bg-muted/40 text-sm text-fg-muted" role="alert">
      <span className="inline-flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-status-warning shrink-0" />
        {t('No se pudo cargar {{label}}. Revisa tu conexión.', { label: label || t('esta sección') })}
      </span>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="ml-3 inline-flex items-center gap-1.5 text-brand-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
      >
        <RefreshCw className="w-3.5 h-3.5" />
        {t('Recargar la página')}
      </button>
    </div>
  );
  return <Boundary fallback={fallback}>{children}</Boundary>;
}

/**
 * Envuelve la factory de un import() lazy con UN reintento (backoff corto):
 * React.lazy cachea el rechazo para siempre, así que un blip de red al primer
 * intento dejaría el chunk marcado como roto hasta recargar. Con el reintento,
 * el caso transitorio se autorrepara y el boundary queda solo para fallos reales.
 */
export function lazyRetryImport(factory) {
  return () =>
    factory().catch(
      () => new Promise((res) => setTimeout(res, 1500)).then(factory),
    );
}
